import React from 'react';
import styles from '../styles.scss';

const winkAnim = (elements) => {
  const { head, leftEye, rightEye } = elements;

  head.classList.add(styles.appearTopToBottom);

  setTimeout(() => {
    leftEye.classList.add(styles.wink);
    rightEye.classList.add(styles.wink);
  }, 300);

  setTimeout(() => {
    head.classList.add(styles.disappearBottomToTop);
  }, 2500);

  setTimeout(() => {
    head.classList.remove(styles.appearTopToBottom, styles.disappearBottomToTop);
    leftEye.classList.remove(styles.wink);
    rightEye.classList.remove(styles.wink);
  }, 3000);
};

const glancesAnim = (elements) => {
  const { head, leftEye, rightEye } = elements;

  head.classList.add(styles.appearTopToBottom);

  setTimeout(() => {
    leftEye.classList.add(styles.glances);
    rightEye.classList.add(styles.glances);
  }, 300);

  setTimeout(() => {
    head.classList.add(styles.disappearBottomToTop);
  }, 5000);

  setTimeout(() => {
    head.classList.remove(styles.appearTopToBottom, styles.disappearBottomToTop);
    leftEye.classList.remove(styles.glances);
    rightEye.classList.remove(styles.glances);
  }, 5300);
};

const blinkAnim = (elements) => {
  const { head, leftEye, rightEye } = elements;

  head.classList.add(styles.appearTopToBottom);

  setTimeout(() => {
    leftEye.classList.add(styles.blink);
    rightEye.classList.add(styles.blink);
  }, 300);

  setTimeout(() => {
    head.classList.add(styles.disappearBottomToTop);
  }, 5500);

  setTimeout(() => {
    head.classList.remove(styles.appearTopToBottom, styles.disappearBottomToTop);
    leftEye.classList.remove(styles.blink);
    rightEye.classList.remove(styles.blink);
  }, 6000);
};

const animations = [blinkAnim, winkAnim, glancesAnim];

export const pickRandomAnimation = () => {
  const doAnimation = Math.random() < 0.3;
  if (!doAnimation) return null;

  return animations[Math.floor(Math.random() * animations.length)];
};

export const runRandomAnimation = (refs) => {
  const animation = pickRandomAnimation();

  if (!animation) return;

  const { headRef, leftEyeRef, rightEyeRef } = refs;

  if (!headRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

  const isHeadAnimating = headRef.current.getAnimations().some(anim => !anim.finished);
  const isLeftEyeAnimating = leftEyeRef.current.getAnimations().some(anim => !anim.finished);
  const isRightEyeAnimating = rightEyeRef.current.getAnimations().some(anim => !anim.finished);

  if (isHeadAnimating || isLeftEyeAnimating || isRightEyeAnimating) return;

  animation({
    head: headRef.current,
    leftEye: leftEyeRef.current,
    rightEye: rightEyeRef.current,
  });
};

export const useRunAnimation = () => {
  const headRef = React.useRef(null);
  const leftEyeRef = React.useRef(null);
  const rightEyeRef = React.useRef(null);

  React.useEffect(() => {
    const initialDelay = 1 * 60 * 1000; // 1 minute
    const intervalRef = { current: null };

    const initialTimeout = setTimeout(() => {
      const runAnimation = () => runRandomAnimation({ headRef, leftEyeRef, rightEyeRef });

      runAnimation();
      const interval = setInterval(runAnimation, 3 * 60 * 1000); // 3 minutes

      intervalRef.current = interval;
    }, initialDelay);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    headRef,
    leftEyeRef,
    rightEyeRef,
  };
};

const SVG = ({
  fillColor,
  wordMarkColor = '#333',
  brandMarkColor = '#e41321',
}) => {
  const { headRef, leftEyeRef, rightEyeRef } = useRunAnimation();

  return (
    <div className={styles.logoContainer}>
      <div className={styles.head} ref={headRef} data-testid="ds-animated-logo-head">
        <div className={styles.rightEye} ref={rightEyeRef} />
        <div className={styles.leftEye} ref={leftEyeRef} />
      </div>
      <svg
        role="img"
        data-testid="ds-logo-animated"
        viewBox="0 0 197 40"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.svg}
      >
        <title>Redbubble logo</title>
        <path
          data-testid="ds-wordmark"
          fill={fillColor || wordMarkColor}
          d="m68.434 34.027-0.11764-3.2012c-0.062742-1.7555-0.19868-5.8429-0.19868-5.8429-0.078428-2.5215-0.59605-4.4024-3.1044-5.0821v-0.12026c2.1097-0.84049 3.5815-3.2025 3.5815-6.4023 0-5.0442-2.3881-7.3644-7.0062-7.3644h-7.0847v28.013h4.5371v-11.525h2.1894c1.7908 0 2.4287 1.081 2.4679 2.9607l0.16078 5.7645a123.59 123.59 0 0 1 0.0366 2.7999h4.5384zm-4.4965-18.408c-0.039214 1.6405-0.59605 3.3619-2.7463 3.3619h-2.1515v-8.8846h2.1894c2.0313 0 2.7868 1.8012 2.7476 3.3214l-0.039214 2.2012zm133.81 18.408v-4.1619h-7.2846v-8.3238h5.9318v-4.1619h-5.9318v-7.2036h7.286v-4.1619h-11.824v28.013h11.824-0.001307zm-14.461 0v-4.1619h-7.286v-23.851h-4.5384v28.013h11.824zm-20.76 0c3.3057 0 6.171-2.4012 6.171-7.963 0-3.2822-1.6313-5.5632-3.8221-6.3239v-0.15816c2.2705-0.96074 3.6639-3.2809 3.6639-6.3631 0-5.2024-2.5489-7.2036-6.4102-7.2036h-7.6833v28.013h8.0807v-0.001307zm1.2745-19.049c-0.039214 1.9999-1.3542 2.9214-2.5489 2.9214h-2.3084v-7.8036h2.23c1.911 0 2.7058 0.92022 2.6666 3.1606l-0.039214 1.7215zm0.2392 11.605c0 2.2417-0.91499 3.4025-2.9071 3.4025h-2.1894v-8.4049h2.0705c1.911 0 3.026 1.362 3.026 3.2025v1.7999zm-18.546 7.4441c3.3044 0 6.1697-2.4012 6.1697-7.963 0-3.2822-1.6313-5.5632-3.8207-6.3239v-0.15816c2.2692-0.96074 3.6626-3.2809 3.6626-6.3631 0-5.2024-2.5489-7.2036-6.4102-7.2036h-7.6833v28.013h8.082v-0.001307zm1.2745-19.049c-0.040521 1.9999-1.3542 2.9214-2.5489 2.9214h-2.3084v-7.8036h2.2287c1.911 0 2.7071 0.92022 2.6666 3.1606l-0.039214 1.7215h0.001307zm0.2379 11.605c0 2.2417-0.91499 3.4025-2.9058 3.4025h-2.1894v-8.4049h2.0692c1.911 0 3.026 1.362 3.026 3.2025v1.7999zm-19.706 7.9657c4.8168 0 6.886-3.162 6.886-7.5239v-21.011h-4.2992v20.889c0 1.762-0.43789 3.5227-2.4679 3.5227-2.1502 0-2.5489-2.0417-2.5489-3.4417v-20.97h-4.5384v21.33c0 4.8429 2.5489 7.2023 6.967 7.2023l0.001307 0.002614zm-15.607-0.52024c3.3044 0 6.171-2.4012 6.171-7.963 0-3.2822-1.6326-5.5632-3.8221-6.3239v-0.15947c2.2692-0.96074 3.6626-3.2809 3.6626-6.3631 0-5.2024-2.5489-7.2036-6.4089-7.2036h-7.6846v28.013h8.082zm1.2745-19.049c-0.039214 1.9999-1.3542 2.9214-2.5489 2.9214h-2.3084v-7.8036h2.2287c1.911 0 2.7084 0.92022 2.6679 3.1606l-0.039214 1.7215zm0.2379 11.605c0 2.2417-0.91499 3.4025-2.9058 3.4025h-2.1894v-8.4049h2.0705c1.911 0 3.0247 1.362 3.0247 3.2025v1.7999zm-19.348 7.4441c4.1397 0 6.4494-2.1607 6.4494-6.7226v-14.73c0-4.4429-2.3489-6.5631-6.5696-6.5631h-7.4441v28.013h7.5644v0.002614zm1.8705-6.9226c0 1.7607-0.55684 2.7999-2.7868 2.7999h-2.1097v-19.77h2.1502c2.2287 0 2.7868 1.3202 2.7868 2.8012l-0.039214 14.168-0.001307 0.001307zm-24.166 6.92v-28.012h11.824v4.1619h-7.286v7.2023h5.9318v4.1645h-5.9318v8.3238h7.286v4.1619h-11.824v-0.002614z"
        />
        <path
          data-testid="ds-brandmark"
          fill={fillColor || brandMarkColor}
          d="m27.621 27.225h-5.3279a0.53462 0.53462 0 0 1-0.53331-0.53592v-13.372c0-0.29541 0.2392-0.53462 0.53331-0.53462h4.9397c3.707 0 4.4848 2.1829 4.4848 4.0129 0 1.0588-0.28103 1.8993-0.83918 2.5162 1.3594 0.56076 2.0914 1.8248 2.0914 3.6534 0 2.6679-1.9999 4.2599-5.3488 4.2599m-7.7356 0h-11.066a0.53331 0.53331 0 0 1-0.532-0.53592v-13.372c0-0.29541 0.2379-0.53462 0.532-0.53462h5.1697c3.2286 0 5.1553 1.7385 5.1553 4.6508 0 1.9372-0.97381 3.4587-2.5437 4.0769l3.6822 4.8246a0.53592 0.53592 0 0 1-0.39868 0.88885m0.019607-27.188c-11.037 0-19.986 8.9486-19.986 19.986 0 11.04 8.9486 19.989 19.987 19.989s19.986-8.9486 19.986-19.987-8.946-19.986-19.985-19.986"
        />
      </svg>
    </div>
  );
};

export default SVG;
